import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

type Sequence = {
  id: number
  object_type: string
  object_label: string
  scope: 'subsidiary' | 'global' | 'unknown'
  tier: 'reference' | 'operational' | 'document'
  subsidiary: number | null
  subsidiary_code: string | null
  subsidiary_name: string | null
  prefix: string
  suffix: string
  padding: number
  next_value: number
  reset_period: 'NEVER' | 'YEARLY' | 'MONTHLY'
  last_reset_on: string | null
  active: boolean
  preview: string
}

type SequenceInput = {
  prefix: string
  suffix: string
  padding: number
  next_value: number
  reset_period: 'NEVER' | 'YEARLY' | 'MONTHLY'
  active: boolean
}

// ── Tier metadata ──────────────────────────────────────────────────────────

type Tier = 'reference' | 'operational' | 'document'

const TIER_META: Record<Tier, { title: string; description: string; badge: string }> = {
  reference: {
    title: 'Reference Codes',
    description:
      'Stable master-data identifiers (departments, positions, partners, etc.). ' +
      'No gap requirement — optional at creation; the record page lets you fill them in later.',
    badge: 'bg-gray-100 text-gray-600',
  },
  operational: {
    title: 'Operational Numbers',
    description:
      'Tracking numbers for people and work (employee numbers, engagements, jobs, ' +
      'requests, etc.). Required on creation; gaps are undesirable but not a legal risk.',
    badge: 'bg-blue-50 text-blue-700',
  },
  document: {
    title: 'Financial Document Numbers',
    description:
      'Formal document numbers (invoices, bills, sales orders, etc.). These carry legal ' +
      'significance in most jurisdictions and will move to a dedicated Document Series ' +
      'system in a future release. Configure them here for now.',
    badge: 'bg-amber-50 text-amber-700',
  },
}

const TIER_ORDER: Tier[] = ['reference', 'operational', 'document']

// App labels within a tier group
const APP_LABELS: Record<string, string> = {
  finance: 'Finance',
  sales: 'Sales',
  hr: 'HR',
  inventory: 'Inventory',
  consulting: 'Consulting',
  ats: 'ATS (Recruiting)',
  appraisals: 'Appraisals',
  request_hub: 'Requests',
  auth: 'Users',
  procurement: 'Procurement',
}

function appOf(object_type: string): string {
  return object_type.split('.')[0]
}

export default function NumberingAdmin() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['number-sequences'],
    queryFn: async () => {
      const r = await api.get('/number-sequences/')
      const body = r.data
      if (Array.isArray(body)) return body as Sequence[]
      if (body && Array.isArray(body.results)) return body.results as Sequence[]
      return [] as Sequence[]
    },
  })

  const [editing, setEditing] = useState<Sequence | null>(null)

  const toggleMut = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) =>
      (await api.patch(`/number-sequences/${id}/`, { active })).data,
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: ['number-sequences'] })
      const previous = qc.getQueryData<Sequence[]>(['number-sequences'])
      qc.setQueryData<Sequence[]>(['number-sequences'], (old) =>
        (old || []).map((s) => (s.id === id ? { ...s, active } : s)),
      )
      return { previous }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['number-sequences'], ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['number-sequences'] }),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload, force }: { id: number; payload: SequenceInput; force: boolean }) =>
      (await api.patch(`/number-sequences/${id}/`, { ...payload, force_prefix_change: force || undefined })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['number-sequences'] })
      setEditing(null)
    },
  })

  const resetMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/number-sequences/${id}/reset/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['number-sequences'] }),
  })

  const sequences = data || []

  // Group by tier → app
  const byTierApp: Record<Tier, Record<string, Sequence[]>> = {
    reference: {},
    operational: {},
    document: {},
  }
  sequences.forEach((s) => {
    const tier = (s.tier || 'operational') as Tier
    const app = appOf(s.object_type)
    if (!byTierApp[tier][app]) byTierApp[tier][app] = []
    byTierApp[tier][app].push(s)
  })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-gray-800">
          Numbering ({sequences.length})
        </h2>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Configure auto-generated codes and numbers across the platform. When a
        sequence is active, leaving the field blank on a create form allocates
        the next value. Disable a sequence to require manual entry instead.{' '}
        <span className="text-gray-400">
          Prefix/suffix placeholders:{' '}
          {['{YYYY}', '{YY}', '{MM}', '{SUB}'].map((p) => (
            <code key={p} className="text-xs bg-gray-100 px-1 rounded mr-1">{p}</code>
          ))}
        </span>
      </p>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : sequences.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No sequences configured yet.
        </div>
      ) : (
        <div className="space-y-8">
          {TIER_ORDER.map((tier) => {
            const appMap = byTierApp[tier]
            if (Object.keys(appMap).length === 0) return null
            const meta = TIER_META[tier]
            const appKeys = Object.keys(appMap).sort((a, b) =>
              (APP_LABELS[a] || a).localeCompare(APP_LABELS[b] || b),
            )
            return (
              <div key={tier}>
                {/* Tier header */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-gray-800">{meta.title}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.badge}`}>
                      {tier}
                    </span>
                    {tier === 'document' && (
                      <span className="text-xs text-amber-600 font-medium">
                        ⚠ Future: DocumentSeries
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{meta.description}</p>
                </div>

                {/* App sub-groups */}
                <div className="space-y-4">
                  {appKeys.map((appKey) => (
                    <div key={appKey} className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">
                          {APP_LABELS[appKey] || appKey}
                        </h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium">Object</th>
                              <th className="text-left px-4 py-2 font-medium">Scope</th>
                              <th className="text-left px-4 py-2 font-medium">Pattern preview</th>
                              <th className="text-right px-4 py-2 font-medium">Next #</th>
                              <th className="text-left px-4 py-2 font-medium">Reset</th>
                              <th className="text-center px-4 py-2 font-medium">Active</th>
                              <th className="text-right px-4 py-2 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {appMap[appKey].map((s) => (
                              <tr key={s.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-800">{s.object_label}</div>
                                  <div className="text-xs text-gray-400 font-mono">{s.object_type}</div>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {s.subsidiary_code ? (
                                    <span className="text-gray-700">{s.subsidiary_code}</span>
                                  ) : (
                                    <span className="text-gray-400 italic">global</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 font-mono text-gray-700">
                                  {s.preview || <span className="text-gray-400">—</span>}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                                  {s.next_value}
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {s.reset_period === 'NEVER' ? '—' : s.reset_period.toLowerCase()}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <Toggle
                                    checked={s.active}
                                    onChange={(v) => toggleMut.mutate({ id: s.id, active: v })}
                                  />
                                </td>
                                <td className="px-4 py-3 text-right space-x-3 shrink-0">
                                  <button
                                    onClick={() => setEditing(s)}
                                    className="text-blue-600 hover:underline text-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Reset counter for "${s.object_label}" back to 1?\n\nThis can produce duplicate codes if records using prior numbers still exist.`,
                                        )
                                      ) {
                                        resetMut.mutate(s.id)
                                      }
                                    }}
                                    className="text-red-600 hover:underline text-sm"
                                  >
                                    Reset
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editing && (
        <SequenceDialog
          sequence={editing}
          submitting={updateMut.isPending}
          error={updateMut.error as any}
          onCancel={() => setEditing(null)}
          onSubmit={(payload, force) => updateMut.mutate({ id: editing.id, payload, force })}
        />
      )}
    </AdminLayout>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

function SequenceDialog({
  sequence,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  sequence: Sequence
  onSubmit: (payload: SequenceInput, force: boolean) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<SequenceInput>({
    prefix: sequence.prefix,
    suffix: sequence.suffix,
    padding: sequence.padding,
    next_value: sequence.next_value,
    reset_period: sequence.reset_period,
    active: sequence.active,
  })
  const [force, setForce] = useState(false)
  const set = <K extends keyof SequenceInput>(k: K, v: SequenceInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const livePreview = previewPattern(form.prefix, form.suffix, form.padding, form.next_value, sequence.subsidiary_code)

  // Detect if prefix/suffix are being changed on an already-used sequence
  const prefixChanged = form.prefix !== sequence.prefix || form.suffix !== sequence.suffix
  const isUsed = sequence.next_value > 1
  const showForceToggle = prefixChanged && isUsed

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form, force)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-gray-800">Edit numbering — {sequence.object_label}</h3>
        <p className="text-xs text-gray-500 font-mono mt-0.5 mb-4">
          {sequence.object_type}
          {sequence.subsidiary_code ? ` · ${sequence.subsidiary_code}` : ' · global'}
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 mb-4">
          <div className="text-xs text-gray-500 mb-1">Live preview</div>
          <div className="font-mono text-base text-gray-800">{livePreview}</div>
        </div>

        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="seq-prefix">Prefix</FieldLabel>
                <Input
                  id="seq-prefix"
                  value={form.prefix}
                  onChange={(e) => set('prefix', e.target.value)}
                  placeholder="INV-{YYYY}-"
                  className="font-mono"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="seq-suffix">Suffix</FieldLabel>
                <Input
                  id="seq-suffix"
                  value={form.suffix}
                  onChange={(e) => set('suffix', e.target.value)}
                  className="font-mono"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="seq-padding">Padding</FieldLabel>
                  <Input
                    id="seq-padding"
                    type="number"
                    min={1}
                    max={10}
                    value={form.padding}
                    onChange={(e) => set('padding', Number(e.target.value || 1))}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="seq-next">Next value</FieldLabel>
                  <Input
                    id="seq-next"
                    type="number"
                    min={1}
                    value={form.next_value}
                    onChange={(e) => set('next_value', Number(e.target.value || 1))}
                  />
                </Field>
              </div>
              <Field>
                <FieldLabel htmlFor="seq-reset">Reset period</FieldLabel>
                <Select
                  value={form.reset_period}
                  onValueChange={(v) =>
                    set('reset_period', v as SequenceInput['reset_period'])
                  }
                >
                  <SelectTrigger id="seq-reset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEVER">Never</SelectItem>
                    <SelectItem value="YEARLY">Yearly</SelectItem>
                    <SelectItem value="MONTHLY">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Active (auto-allocate)</span>
              </label>

              {showForceToggle && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <strong>Warning:</strong> This sequence has already allocated{' '}
                  {sequence.next_value - 1} code(s). Changing the prefix or suffix will
                  make new codes look different from existing ones.
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={force}
                      onChange={(e) => setForce(e.target.checked)}
                    />
                    <span className="font-medium">I understand — apply the change anyway</span>
                  </label>
                </div>
              )}
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || (showForceToggle && !force)}
            >
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function previewPattern(
  prefix: string,
  suffix: string,
  padding: number,
  nextValue: number,
  subsidiaryCode: string | null,
): string {
  const now = new Date()
  const yyyy = String(now.getFullYear())
  const yy = yyyy.slice(-2)
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const sub = subsidiaryCode || ''
  const expand = (s: string): string =>
    s
      .replaceAll('{YYYY}', yyyy)
      .replaceAll('{YY}', yy)
      .replaceAll('{MM}', mm)
      .replaceAll('{SUB}', sub)
  const counter = String(Math.max(1, nextValue || 1)).padStart(Math.max(1, padding || 1), '0')
  return `${expand(prefix)}${counter}${expand(suffix)}`
}
